/*
  assets/littleg-progress.js — GAME-BOUNDARY-14-place-gem-progress
  Updated: 2026-09-03

  Owner:
  - Family-local Mission completion, Gem display, Badge display and local progress.
  - Aligned with GCityPartnersServiceLaneMap & LittleGResultStorage.

  Canonical family flow:
  Place -> Mission -> littleg:mission-state -> Gem -> littleg:mission-complete.

  Boundary:
  - no Structured Observation / Evidence / Signal ownership
  - no Place Record / professional verification ownership
  - no GFun wallet / balance / ledger ownership
*/
(function (window, document) {
  'use strict';

  var VERSION = '2026-09-03-place-gem-progress-08';
  var NS = 'littleg.progress.v1';
  var LEGACY_NS = 'littleg.placePreview.v1';
  var MAX_QUEUE_LENGTH = 80;

  if (window.LittleGProgress && window.LittleGProgress.version === VERSION) {
    return;
  }

  var KEYS = Object.freeze({
    active: NS + '.active',
    progress: NS + '.progress',
    shareQueue: NS + '.shareQueue'
  });

  var LEGACY_KEYS = Object.freeze({
    active: LEGACY_NS + '.active',
    progress: LEGACY_NS + '.progress',
    shareQueue: LEGACY_NS + '.shareQueue'
  });

  function nowIso() {
    return new Date().toISOString();
  }

  function clean(value, fallback) {
    var text = String(value == null ? '' : value).trim();
    return text || (fallback || '');
  }

  function slug(value, fallback) {
    var text = clean(value, fallback)
      .toLowerCase()
      .replace(/[_\s]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    return text || clean(fallback);
  }

  function canonicalLane(value) {
    var raw = clean(value);
    if (!raw) return '';
    if (window.GCityPartnersServiceLaneMap && typeof window.GCityPartnersServiceLaneMap.canonical === 'function') {
      return window.GCityPartnersServiceLaneMap.canonical(raw) || slug(raw);
    }
    return slug(raw);
  }

  function normalizeGems(value, fallback) {
    if (typeof value === 'string') value = value.replace(/[^0-9.-]/g, '');
    var num = Number(value);
    var alt = Number(fallback || 0);
    var finalVal = isFinite(num) ? num : (isFinite(alt) ? alt : 0);
    return Math.max(0, finalVal);
  }

  function normalizeArray(list) {
    if (!Array.isArray(list)) return [];
    return list.map(function (item) { return clean(item); }).filter(Boolean);
  }

  function uniqPush(list, value) {
    var next = normalizeArray(list);
    var val = clean(value);
    if (val && next.indexOf(val) === -1) next.push(val);
    return next;
  }

  function uniqueByKey(list, item, keyName) {
    var next = Array.isArray(list) ? list.slice() : [];
    var id = item && clean(item[keyName]);
    if (!id) return next;

    var replaced = false;
    next = next.map(function (existing) {
      if (existing && clean(existing[keyName]) === id) {
        replaced = true;
        return Object.assign({}, existing, item);
      }
      return existing;
    });

    if (!replaced) next.push(item);
    return next;
  }

  function clampPercent(value) {
    var num = Number(value);
    return Math.max(0, Math.min(100, isFinite(num) ? num : 0));
  }

  function storageGet(keyName) {
    try { return window.localStorage.getItem(keyName); }
    catch (_) { return null; }
  }

  function storageSet(keyName, value) {
    try {
      window.localStorage.setItem(keyName, JSON.stringify(value));
      return true;
    } catch (_) { return false; }
  }

  function storageRemove(keyName) {
    try {
      window.localStorage.removeItem(keyName);
      return true;
    } catch (_) { return false; }
  }

  function read(keyName, fallback, legacyKey) {
    var raw = storageGet(keyName);
    if (raw !== null) {
      try { return JSON.parse(raw); } catch (_) {}
    }
    if (legacyKey) {
      var legacyRaw = storageGet(legacyKey);
      if (legacyRaw !== null) {
        try { return JSON.parse(legacyRaw); } catch (_) {}
      }
    }
    return fallback;
  }

  function write(keyName, value) {
    return storageSet(keyName, value);
  }

  function dispatch(name, detail) {
    if (!name || typeof window === 'undefined') return false;
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
      return true;
    } catch (_) { return false; }
  }

  function baseProgress() {
    return {
      gems: 0,
      completedMissions: [],
      visitedPlaces: [],
      unlockedIslands: [],
      badges: [],
      badgeDetails: [],
      islandProgress: {},
      updatedAt: null
    };
  }

  function legacyGemValue(progress) {
    if (!progress || typeof progress !== 'object') return 0;
    if (progress.gems !== undefined) return normalizeGems(progress.gems, 0);
    if (progress.localRewardPoints !== undefined) return normalizeGems(progress.localRewardPoints, 0);
    if (progress.local_reward_points !== undefined) return normalizeGems(progress.local_reward_points, 0);
    if (progress.localReward !== undefined) return normalizeGems(progress.localReward, 0);
    if (progress.gfun !== undefined) return normalizeGems(progress.gfun, 0);
    return 0;
  }

  function normalizeBadgeDetails(list) {
    if (!Array.isArray(list)) return [];
    return list.filter(function (item) {
      return item && typeof item === 'object' && clean(item.key);
    }).map(function (item) {
      return {
        key: clean(item.key),
        badge: clean(item.badge),
        placeId: clean(item.placeId || item.place_id),
        place: clean(item.place || item.place_name || item.placeName),
        roundKey: clean(item.roundKey || item.round_key),
        mission: clean(item.mission),
        missionKey: clean(item.missionKey || item.mission_key),
        island: canonicalLane(item.island),
        gemReward: normalizeGems(item.gemReward || item.gem_reward || item.gems || item.reward, 0),
        image: clean(item.image),
        ctaUrl: clean(item.ctaUrl || item.cta_url),
        unlockedAt: clean(item.unlockedAt || item.unlocked_at)
      };
    });
  }

  function normalizeProgress(input) {
    var progress = input && typeof input === 'object' ? input : baseProgress();
    var islandProgress = progress.islandProgress && typeof progress.islandProgress === 'object'
      ? progress.islandProgress
      : {};
    var normalizedIslandProgress = {};

    Object.keys(islandProgress).forEach(function (k) {
      var island = canonicalLane(k);
      if (island) normalizedIslandProgress[island] = clampPercent(islandProgress[k]);
    });

    return {
      gems: legacyGemValue(progress),
      completedMissions: normalizeArray(progress.completedMissions),
      visitedPlaces: normalizeArray(progress.visitedPlaces),
      unlockedIslands: normalizeArray(progress.unlockedIslands).map(canonicalLane).filter(Boolean),
      badges: normalizeArray(progress.badges),
      badgeDetails: normalizeBadgeDetails(progress.badgeDetails),
      islandProgress: normalizedIslandProgress,
      updatedAt: clean(progress.updatedAt) || null
    };
  }

  function buildMissionKey(placeId, roundKey, mission) {
    return [slug(placeId, 'place'), slug(roundKey || mission, 'mission')].join(':');
  }

  function buildBadgeKey(placeId, missionKey, badge) {
    return [slug(placeId, 'place'), slug(missionKey, 'mission'), slug(badge, 'observer')].join(':');
  }

  function gemRewardFrom(input) {
    return normalizeGems(input && (input.gemReward || input.gem_reward || input.gems || input.reward), 0);
  }

  function getActive() {
    var active = read(KEYS.active, {}, LEGACY_KEYS.active);
    return active && typeof active === 'object' ? active : {};
  }

  function setActive(input) {
    input = input || {};
    var active = {
      placeId: clean(input.placeId || input.place_id),
      place: clean(input.place || input.place_name || input.placeName),
      roundKey: clean(input.roundKey || input.round_key),
      mission: clean(input.mission || input.mission_handle || input.missionHandle),
      missionKey: clean(input.missionKey || input.mission_key),
      island: canonicalLane(input.island || input.island_handle || input.islandHandle || input.service_lane),
      ctaUrl: clean(input.cta_url || input.ctaUrl || input.url),
      image: clean(input.image),
      gemReward: gemRewardFrom(input),
      updatedAt: nowIso()
    };

    write(KEYS.active, active);
    dispatch('littleg:active-updated', {
      source: 'littleg-progress',
      active: active,
      updatedAt: active.updatedAt
    });
    return active;
  }

  function getProgress() {
    return normalizeProgress(read(KEYS.progress, baseProgress(), LEGACY_KEYS.progress));
  }

  function saveProgress(progress) {
    var next = normalizeProgress(progress);
    next.updatedAt = nowIso();
    var persisted = write(KEYS.progress, next);

    dispatch('littleg:progress-updated', {
      source: 'littleg-progress',
      progress: next,
      persisted: persisted,
      updatedAt: next.updatedAt
    });

    return next;
  }

  function getIslandCompletion(progress, island) {
    var keyName = canonicalLane(island);
    if (!keyName || !progress || !progress.islandProgress) return 0;
    return clampPercent(progress.islandProgress[keyName]);
  }

  function completeMission(input) {
    input = input || {};
    var active = getActive();
    var placeId = clean(input.placeId || input.place_id || active.placeId);
    var place = clean(input.place || input.place_name || input.placeName || active.place);
    var roundKey = clean(input.roundKey || input.round_key || active.roundKey);
    var mission = clean(input.mission || input.mission_handle || input.missionHandle || active.mission);
    var island = canonicalLane(input.island || input.island_handle || input.islandHandle || input.service_lane || active.island);

    if (!placeId || !mission) {
      var invalid = {
        source: 'littleg-progress',
        reason: 'missing_place_or_mission',
        placeId: placeId,
        mission: mission,
        progress: getProgress(),
        rejectedAt: nowIso()
      };
      dispatch('littleg:mission-complete-rejected', invalid);
      return invalid.progress;
    }

    var missionKey = clean(
      input.missionKey || input.mission_key || active.missionKey,
      buildMissionKey(placeId, roundKey, mission)
    );
    var badge = slug(input.badge, 'place-observer');
    var badgeKey = buildBadgeKey(placeId, missionKey, badge);
    var gemReward = gemRewardFrom(input);
    var progress = getProgress();

    if (progress.completedMissions.indexOf(missionKey) !== -1) return progress;

    var completedAt = nowIso();
    progress.completedMissions = uniqPush(progress.completedMissions, missionKey);
    progress.visitedPlaces = uniqPush(progress.visitedPlaces, placeId);

    if (island) {
      progress.unlockedIslands = uniqPush(progress.unlockedIslands, island);
      progress.islandProgress[island] = Math.max(getIslandCompletion(progress, island), 100);
    }

    progress.badges = uniqPush(progress.badges, badgeKey);
    progress.badgeDetails = uniqueByKey(progress.badgeDetails, {
      key: badgeKey,
      badge: badge,
      placeId: placeId,
      place: place,
      roundKey: roundKey,
      mission: mission,
      missionKey: missionKey,
      island: island,
      gemReward: gemReward,
      image: clean(input.image),
      ctaUrl: clean(input.cta_url || input.ctaUrl || input.url),
      unlockedAt: completedAt
    }, 'key');

    progress.gems = Math.max(0, normalizeGems(progress.gems, 0) + gemReward);

    var next = saveProgress(progress);

    dispatch('littleg:mission-complete', {
      source: 'littleg-progress',
      placeId: placeId,
      place: place,
      roundKey: roundKey,
      island: island,
      mission: mission,
      missionKey: missionKey,
      badge: badge,
      badgeKey: badgeKey,
      gemsEarned: gemReward,
      alreadyDone: false,
      progress: next,
      completedAt: completedAt
    });

    return next;
  }

  function pushQueue(keyName, payload, legacyKey) {
    var queue = read(keyName, [], legacyKey);
    if (!Array.isArray(queue)) queue = [];
    queue.push(Object.assign({ createdAt: nowIso() }, payload || {}));
    if (queue.length > MAX_QUEUE_LENGTH) {
      queue = queue.slice(queue.length - MAX_QUEUE_LENGTH);
    }
    write(keyName, queue);
    return queue;
  }

  function normalizeSharePayload(payload) {
    payload = payload || {};
    var active = getActive();
    var id = clean(payload.id, 'share-' + Date.now());

    return Object.assign({}, payload, {
      source: clean(payload.source, 'littleg-progress'),
      id: id,
      placeId: clean(payload.placeId || payload.place_id || active.placeId),
      place: clean(payload.place || payload.place_name || active.place),
      roundKey: clean(payload.roundKey || payload.round_key || active.roundKey),
      island: canonicalLane(payload.island || payload.island_handle || payload.islandHandle || payload.service_lane || active.island),
      mission: clean(payload.mission || payload.mission_handle || payload.missionHandle || active.mission),
      missionKey: clean(payload.missionKey || payload.mission_key || active.missionKey),
      summary: clean(payload.summary || payload.text || payload.title)
    });
  }

  function pushShare(payload) {
    var item = normalizeSharePayload(payload);
    var queue = pushQueue(KEYS.shareQueue, item, LEGACY_KEYS.shareQueue);
    dispatch('littleg:share-ready', item);
    return queue;
  }

  function exportState() {
    return {
      active: getActive(),
      progress: getProgress(),
      shareQueue: read(KEYS.shareQueue, [], LEGACY_KEYS.shareQueue),
      exportedAt: nowIso()
    };
  }

  function repairState() {
    var state = exportState();
    var shareQueue = Array.isArray(state.shareQueue)
      ? state.shareQueue.slice(-MAX_QUEUE_LENGTH).map(normalizeSharePayload)
      : [];

    write(KEYS.active, state.active && typeof state.active === 'object' ? state.active : {});
    write(KEYS.progress, normalizeProgress(state.progress));
    write(KEYS.shareQueue, shareQueue);
    return exportState();
  }

  function hardReset() {
    storageRemove(KEYS.active);
    storageRemove(KEYS.progress);
    storageRemove(KEYS.shareQueue);
    return exportState();
  }

  function consumePlaceMissionReady(event) {
    var detail = event && event.detail && typeof event.detail === 'object' ? event.detail : {};
    var place = detail.place && typeof detail.place === 'object' ? detail.place : {};
    var mission = detail.mission && typeof detail.mission === 'object' ? detail.mission : {};

    setActive({
      placeId: place.id,
      place: place.display_name || place.name,
      roundKey: mission.round_key,
      mission: mission.round_key || mission.title,
      island: canonicalLane(mission.evidence_family || mission.service_lane),
      gemReward: mission.gem_reward
    });
  }

  function consumeMissionState(event) {
    var detail = event && event.detail && typeof event.detail === 'object' ? event.detail : {};
    if (detail.three_clues_complete !== true) return;

    var active = getActive();

    completeMission({
      placeId: active.placeId || detail.place_id,
      place: active.place || detail.place || detail.place_name,
      roundKey: detail.round_key || active.roundKey,
      mission: detail.mission_handle || detail.daily_scene_id || active.mission || detail.round_key,
      island: canonicalLane(active.island || detail.service_lane),
      gemReward: active.gemReward,
      badge: 'place-observer'
    });
  }

  document.addEventListener('littleg:place-mission-ready', consumePlaceMissionReady);
  window.addEventListener('littleg:mission-state', consumeMissionState);

  window.LittleGProgress = Object.freeze({
    version: VERSION,
    namespace: NS,
    legacyNamespace: LEGACY_NS,
    keys: KEYS,
    legacyKeys: LEGACY_KEYS,
    getActive: getActive,
    setActive: setActive,
    getProgress: getProgress,
    saveProgress: saveProgress,
    completeMission: completeMission,
    pushShare: pushShare,
    exportState: exportState,
    repairState: repairState,
    hardReset: hardReset,
    resetCorrupted: repairState
  });

  dispatch('littleg:progress-ready', {
    source: 'littleg-progress',
    namespace: NS,
    legacyNamespace: LEGACY_NS,
    readyAt: nowIso()
  });
})(window, document);